import { ApolloError } from '@apollo/client';
import {
  HHButton,
  HHIconButton,
  HHStack,
} from '@hinge-health/react-component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import Close from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { BaseSyntheticEvent, useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../../utils/bills-utils';
import {
  BillingTypeFilter,
  BillStatusFilter,
  ClientFilter,
  CSVInputFilter,
  DateRangeFilter,
  HoldReasonFilter,
  holdReasonsSelectOptions,
  InsurerFilter,
  PartnershipFilter,
  PaymentTypeFilter,
  SourceFilter,
  SubmissionStatusFilter,
  submissionStatusTypesSelectOptions,
} from '../../../form';
import { RawBillManagementAdvancedFiltersValues } from '../../bill-grid-components/bill-management-types';
import { BillSource } from '../../bill-types';
import { extractIds, formatDateForQuery, parseIds } from '../../utils';
import { BillManagementFiltersPropsWithClientsAndInsurersAndPartnerships } from '../filters-toolbar';
import { AdvancedBillManagementFiltersValidationSchema } from './validation-schema';

export interface AdvancedFiltersProps
  extends BillManagementFiltersPropsWithClientsAndInsurersAndPartnerships {
  holdTypes: string[];
  holdTypesError?: ApolloError;
  filterToggle: () => void;
}

export const AdvancedFilters = ({
  setFilterParams,
  filterParams,
  clients,
  insurers,
  partnerships,
  billingTypes,
  hasError,
  holdTypes,
  holdTypesError,
  filterToggle,
  setPaginationModel,
  onResetSearch,
}: AdvancedFiltersProps): JSX.Element => {
  const [disableHoldReasons, setDisableHoldReasons] = useState(false);
  const [disableSourceId, setDisableSourceId] = useState(true);
  const [disableBillingTypes, setDisableBillingTypes] = useState(false);

  const {
    holdReasons,
    startDate,
    endDate,
    userIds,
    source,
    sourceIds,
    clientIds,
    insurerIds,
    partnershipIds,
    billingTypes: filteredBillingTypes,
    submissionStatusTypes: filteredSubmissionStatusTypes,
    billIds,
    held,
    submitted,
    paymentTypes,
    sourceStartDate,
    sourceEndDate,
  } = filterParams;

  const defaultValues: RawBillManagementAdvancedFiltersValues = {
    holdReasons: holdReasonsSelectOptions(holdReasons),
    userIds,
    source: source ?? BillSource.ALL,
    sourceIds,
    clients: clients.filter(client => clientIds.includes(client.id)),
    insurers: insurers.filter(insurer => insurerIds.includes(insurer.id)),
    partnerships: partnerships.filter(partnership =>
      partnershipIds.includes(partnership.id),
    ),
    billingTypes: billingTypes
      .filter(billingType => filteredBillingTypes.includes(billingType.value))
      .map(billingType => ({
        value: billingType.value,
        label: capitalizeFirstLetterAndSpaceSnakeString(billingType.value),
      })),
    dateRange: [
      dayjs(startDate).isValid() ? dayjs(startDate).toDate() : null,
      dayjs(endDate).isValid() ? dayjs(endDate).toDate() : null,
    ],
    billStatus: { held, submitted },
    submissionStatusTypes: submissionStatusTypesSelectOptions.filter(select =>
      filteredSubmissionStatusTypes.includes(select.value),
    ),
    billIds,
    paymentTypes:
      paymentTypes?.map(code => ({ value: code, label: code })) ?? [],
    sourceDateRange: [
      dayjs(sourceStartDate).isValid() ? dayjs(sourceStartDate).toDate() : null,
      dayjs(sourceEndDate).isValid() ? dayjs(sourceEndDate).toDate() : null,
    ],
  };

  const { handleSubmit, control, formState, reset, resetField, watch } =
    useForm<RawBillManagementAdvancedFiltersValues>({
      mode: 'onChange',
      defaultValues,
      criteriaMode: 'all',
      resolver: yupResolver(
        AdvancedBillManagementFiltersValidationSchema,
      ) as Resolver<RawBillManagementAdvancedFiltersValues>,
    });

  const { errors, isValid } = formState;

  const parseFormValues = (
    data: RawBillManagementAdvancedFiltersValues,
  ): void => {
    const {
      holdReasons,
      dateRange,
      userIds,
      source,
      sourceIds,
      clients,
      insurers,
      billingTypes,
      partnerships,
      billStatus,
      submissionStatusTypes,
      paymentTypes,
      billIds,
      sourceDateRange,
      ...rest
    } = data;

    const formattedData = {
      ...rest,
      userIds: parseIds(userIds),
      source: source === BillSource.ALL ? null : source,
      sourceIds: parseIds(sourceIds),
      holdReasons: holdReasons.map(reason => reason.value),
      startDate: formatDateForQuery(dateRange[0]),
      endDate: formatDateForQuery(dateRange[1]),
      clientIds: extractIds(clients),
      insurerIds: extractIds(insurers),
      partnershipIds: extractIds(partnerships),
      billingTypes: billingTypes.map(type => type.value),
      held: billStatus.held,
      submitted: billStatus.submitted,
      submissionStatusTypes: submissionStatusTypes.map(({ value }) => value),
      billIds: parseIds(billIds),
      paymentTypes: paymentTypes.map(({ value }) => value),
      sourceStartDate: formatDateForQuery(sourceDateRange[0]),
      sourceEndDate: formatDateForQuery(sourceDateRange[1]),
    };

    setFilterParams(formattedData);
    filterToggle();
  };

  const onFormSubmit = (e: BaseSyntheticEvent<object>): void => {
    setPaginationModel({ page: 0, pageSize: 100 });
    handleSubmit(parseFormValues)(e);
  };

  const handleReset = (): void => {
    reset(defaultValues);
    onResetSearch();
  };

  const watchedBillStatus = watch('billStatus');
  const isSubmissionStatusTypesDisabled =
    watchedBillStatus.held || !watchedBillStatus.submitted;

  useEffect(() => {
    const isHoldReasonDisabled = watchedBillStatus.held === false;
    setDisableHoldReasons(isHoldReasonDisabled);
  }, [watchedBillStatus]);

  useEffect(() => {
    if (isSubmissionStatusTypesDisabled) {
      resetField('submissionStatusTypes', {
        defaultValue: [],
      });
    }
  }, [isSubmissionStatusTypesDisabled, resetField]);

  const watchedSource = watch('source');

  useEffect(() => {
    const isSourceIdDisabled = watchedSource === BillSource.ALL;
    const isBillingTypesDisabled =
      watchedSource === BillSource.ADMIN_FEE_CLAIMS;
    setDisableSourceId(isSourceIdDisabled);
    setDisableBillingTypes(isBillingTypesDisabled);
  }, [watchedSource]);

  // Handle bill status changes to reset hold reasons
  const handleBillStatusChange = (): void => {
    resetField('holdReasons', {
      defaultValue: [],
    });
  };

  const handleSourceChange = (source: string | null): void => {
    if (source === null) {
      resetField('sourceIds', {
        defaultValue: [],
      });
    }
    if (source === BillSource.ADMIN_FEE_CLAIMS) {
      resetField('billingTypes', {
        defaultValue: [],
      });
    }
  };

  return (
    <form onSubmit={onFormSubmit}>
      <HHStack direction="row" spacing={2}>
        <HHStack
          direction="row"
          flexWrap="wrap"
          alignItems="flex-end"
          sx={{ gap: 4 }}
        >
          <BillStatusFilter
            control={control}
            errors={errors}
            onBillStatusChange={handleBillStatusChange}
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="billIds"
            label="Bill ID"
            showClearButton={false}
          />
          <DateRangeFilter
            control={control}
            errors={errors}
            name="dateRange"
            labelFrom="DOS From"
            labelTo="DOS To"
            tooltipText="Filter bills where the date of service falls between the two selected dates."
          />
          <HoldReasonFilter
            control={control}
            errors={errors}
            holdTypes={holdTypes}
            holdTypesError={holdTypesError}
            disabled={disableHoldReasons}
          />
          <PaymentTypeFilter control={control} errors={errors} />
          <SourceFilter
            control={control}
            errors={errors}
            onSourceChange={handleSourceChange}
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="sourceIds"
            label="Source ID"
            disabled={disableSourceId}
            showClearButton={true}
          />
          <DateRangeFilter
            control={control}
            errors={errors}
            name="sourceDateRange"
            labelFrom="Source From"
            labelTo="Source To"
            tooltipText="Filter bills where the source date falls between the two selected dates. Uses starts_at for subscriptions, occurred_at for fee-for-service events, and service_date for admin fee claims."
          />
          <CSVInputFilter
            control={control}
            errors={errors}
            name="userIds"
            label="User ID"
            showClearButton={true}
          />
          <SubmissionStatusFilter
            control={control}
            errors={errors}
            disabled={isSubmissionStatusTypesDisabled}
          />
          <ClientFilter
            control={control}
            errors={errors}
            clients={clients}
            hasError={hasError}
          />
          <InsurerFilter
            control={control}
            errors={errors}
            insurers={insurers}
            hasError={hasError}
          />
          <PartnershipFilter
            control={control}
            errors={errors}
            partnerships={partnerships}
            hasError={hasError}
          />
          <BillingTypeFilter
            control={control}
            errors={errors}
            billingTypes={billingTypes}
            disabled={disableBillingTypes}
          />
        </HHStack>
        <HHStack direction="row" spacing={4} alignSelf="flex-end">
          <HHButton size="small" hhVariant="text" onClick={handleReset}>
            Reset
          </HHButton>
          <HHButton
            size="small"
            hhVariant="contained"
            onClick={onFormSubmit}
            disabled={!isValid}
          >
            Apply
          </HHButton>
          <HHIconButton
            hhVariant="variant-bypass"
            size="medium"
            color="default"
            aria-label="close-advanced-filters"
            onClick={filterToggle}
          >
            <Close />
          </HHIconButton>
        </HHStack>
      </HHStack>
    </form>
  );
};
